/**
 * The "cloak" — a full-viewport takeover shown while Otto Agent is working.
 *
 * Rendered as an MUI `Modal` so it traps focus and makes the rest of the app
 * inert (the user can't interact — Otto is in control). It streams Otto's
 * thinking, shows the current step, asks clarifying questions, and always
 * offers "Stop — take back control". During the hand-off phase the scrim fades
 * so the user watches the form fill behind it. Respects reduced-motion.
 */
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { useEffect, useRef, useState, type JSX } from 'react';

import { Markdown } from '../pages/documentation/markdown.js';
import { useAgentic, type AgentEntry } from './AgenticProvider.js';
import { MicButton } from './MicButton.js';

export function CloakOverlay(): JSX.Element | null {
  const { active, status, entries, question, error, reply, stop } = useAgentic();
  const theme = useTheme();
  const feedRef = useRef<HTMLDivElement | null>(null);
  const [replyText, setReplyText] = useState('');

  // Auto-scroll the thinking feed to the newest entry.
  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [entries]);

  useEffect(() => {
    if (status !== 'awaiting-input') setReplyText('');
  }, [status]);

  if (!active) return null;

  const isHandoff = status === 'handoff';
  const isError = status === 'error';

  return (
    <Modal
      open={active}
      onClose={(_e, reason) => {
        // Esc = an escape hatch to take back control; a stray backdrop click must NOT abort.
        if (reason === 'escapeKeyDown') stop();
      }}
      aria-labelledby="otto-cloak-title"
      slotProps={{
        backdrop: {
          sx: {
            // Fade the scrim during hand-off so the form is visible behind it.
            bgcolor: (t) =>
              alpha(t.palette.grey[900], isHandoff ? 0.28 : 0.72),
            backdropFilter: isHandoff ? 'blur(1px)' : 'blur(6px)',
            transition: 'background-color .4s ease, backdrop-filter .4s ease',
          },
        },
      }}
    >
      <Box
        data-testid="otto-cloak"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: isHandoff ? 'flex-start' : 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 4 },
          pointerEvents: 'none', // only the panel is interactive
        }}
      >
        <Box
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: isHandoff ? 560 : 620,
            mt: isHandoff ? 2 : 0,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            bgcolor: 'background.paper',
            boxShadow: 24,
            // Futuristic animated conic border — a slow sweep of the brand hue.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              padding: '1.5px',
              background: (t) =>
                `conic-gradient(from var(--otto-angle, 0deg), ${alpha(t.palette.primary.main, 0.15)}, ${t.palette.secondary.main}, ${t.palette.primary.main}, ${alpha(t.palette.primary.main, 0.15)})`,
              WebkitMask:
                'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              animation: 'otto-spin 6s linear infinite',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            },
            '@keyframes otto-spin': {
              to: { '--otto-angle': '360deg' },
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              background: (t) =>
                `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.06)}, transparent)`,
            }}
          >
            {isError ? (
              <ErrorOutlineRoundedIcon color="error" />
            ) : (
              <CircularProgress size={20} thickness={5} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id="otto-cloak-title" sx={{ fontSize: 15, fontWeight: 700 }}>
                {isError
                  ? 'Otto hit a snag'
                  : isHandoff
                    ? 'Otto is opening the form…'
                    : status === 'awaiting-input'
                      ? 'Otto needs a detail'
                      : 'Otto is working…'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {isError
                  ? 'You have control again — nothing was saved.'
                  : 'Otto has the screen. It won’t save anything — you review and confirm.'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={stop}
              aria-label="Stop and take back control"
              data-testid="otto-cloak-stop"
              sx={{ color: 'text.secondary' }}
            >
              <StopRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Thinking feed (hidden during the light hand-off strip) */}
          {!isHandoff && (
            <Box
              ref={feedRef}
              data-testid="otto-thinking-feed"
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                px: 3,
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {entries.length === 0 && status === 'thinking' ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary', fontStyle: 'italic' }}>
                  Thinking…
                </Typography>
              ) : (
                entries.map((entry, i) => <FeedRow key={i} entry={entry} />)
              )}
              {isError && error && (
                <Typography sx={{ fontSize: 13, color: 'error.main' }}>{error}</Typography>
              )}
            </Box>
          )}

          {/* Clarifying-question reply box */}
          {status === 'awaiting-input' && (
            <Box sx={{ px: 3, pb: 2.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
              {question && (
                <Box
                  data-testid="otto-question"
                  sx={{
                    maxHeight: 280,
                    overflowY: 'auto',
                    mb: 1.5,
                    // Render Otto's markdown (bold, lists, `code`) instead of raw
                    // ** / ` — and keep it compact for the panel.
                    fontSize: 14,
                    lineHeight: 1.5,
                    '& p': { m: 0, mb: 1 },
                    '& p:last-child': { mb: 0 },
                    '& ol, & ul': { my: 0.5, pl: 2.5 },
                    '& li': { mb: 0.25 },
                    '& code': {
                      px: 0.5,
                      py: 0.125,
                      borderRadius: 0.5,
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
                      fontSize: '0.85em',
                    },
                  }}
                >
                  <Markdown source={question} />
                </Box>
              )}
              <Stack direction="row" spacing={1}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && replyText.trim()) {
                      e.preventDefault();
                      reply(replyText);
                    }
                  }}
                  placeholder="Type or speak your answer…"
                  inputProps={{ 'data-testid': 'otto-reply-input' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <MicButton onText={(t) => setReplyText((p) => (p.trim() ? `${p.trim()} ` : '') + t)} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => reply(replyText)}
                  disabled={!replyText.trim()}
                  data-testid="otto-reply-send"
                >
                  Send
                </Button>
              </Stack>
            </Box>
          )}

          {/* Error dismiss */}
          {isError && (
            <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={stop} data-testid="otto-error-dismiss">
                Take back control
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

function FeedRow({ entry }: { entry: AgentEntry }): JSX.Element {
  const theme = useTheme();
  if (entry.kind === 'thinking') {
    return (
      <Typography sx={{ fontSize: 13.5, color: 'text.primary', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
        {entry.text}
      </Typography>
    );
  }
  if (entry.kind === 'note') {
    return (
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>
        {entry.text}
      </Typography>
    );
  }
  // step | tool
  const status = entry.kind === 'tool' ? entry.status : entry.status;
  const done = status === 'done';
  const errored = status === 'error';
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {done ? (
        <CheckRoundedIcon sx={{ fontSize: 15, color: 'success.main' }} />
      ) : errored ? (
        <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: 'error.main' }} />
      ) : (
        <CircularProgress size={12} thickness={6} />
      )}
      <Typography
        sx={{
          fontSize: 13,
          color: errored ? 'error.main' : theme.palette.text.secondary,
          textDecoration: 'none',
        }}
      >
        {entry.label}
      </Typography>
    </Stack>
  );
}
